<template>
  <div class="mobile-course-carousel">
    <div class="mobile-course-carousel__link">
      <div
        @click="searchTopic(tag)"
        class="mobile-course-carousel__link-wrapper"
      >
        <h3 class="mobile-course-carousel__link">{{ customTitle || tag }}</h3>
      </div>
    </div>
    <Swiper
      class="mobile-course-carousel__swiper-container"
      :swiperOptions="swiperOption"
      :parentClass="swiperClass"
    >
      <div
        v-for="course in courses"
        :key="course.objectID"
        class="swiper-slide mobile-course-carousel__tile"
        :style="
          `background-image: url(${$getCourseBackground(
            course.objectID,
            'small'
          )}), linear-gradient(${$getGradient()})`
        "
      >
        <router-link
          :to="$buildContentLink('course', course)"
          class="mobile-course-carousel__course-link"
        >
          <div class="mobile-course-carousel__overlay"></div>
          <div class="mobile-course-carousel__details">
            <div
              v-if="course.has_sample_content && isFreeTrialUser"
              class="mobile-course-carousel__snipe"
            >
              Included
            </div>
            <div
              v-if="course.status === 'coming soon'"
              class="mobile-course-carousel__snipe"
            >
              Coming Soon
            </div>
            <div class="mobile-course-carousel__title">
              {{ course.name }}
            </div>
            <div
              v-if="course.duration"
              class="mobile-course-carousel__statistics-wrapper"
            >
              <div class="mobile-course-carousel__course-length">
                {{ courseDuration(course) }}
              </div>
            </div>
            <div class="mobile-course-carousel__tile-bottom">
              <div class="mobile-course-carousel__info-wrapper">
                <div class="mobile-course-carousel__instructor-wrapper">
                  <p class="mobile-course-carousel__instructor-name">
                    {{ getInstructor(course) }}
                  </p>
                </div>
                <div
                  :style="getLearningAreaColor(course)"
                  class="mobile-course-carousel__vendor-wrapper"
                >
                  <p class="mobile-course-carousel__learning-area">
                    {{ getLearningArea(course) }}
                  </p>
                  <p class="mobile-course-carousel__vendor-name">
                    {{ getVendor(course) }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </router-link>
      </div>
    </Swiper>
    <div
      v-if="tag"
      class="mobile-course-carousel__view-all"
      @click="searchTopic(tag)"
    >
      View all
    </div>
  </div>
</template>

<script>
import Swiper from '@/components/layout/Swiper.vue'
import { createAlgoliaIndices } from '@/utils'
import { getLearningAreaSnipeColor, getEntities, duration } from '@/helpers'

export default {
  name: 'MobileCourseCarousel',
  data() {
    return {
      swiperOption: {
        slidesPerView: 'auto',
        spaceBetween: 10,
        freeMode: true,
        loop: false,
      },
      courses: [],
    }
  },
  props: {
    tag: {
      type: String,
      required: true,
    },
    algoliaKeys: {
      type: Object,
      required: true,
    },
    isFreeTrialUser: {
      type: Boolean,
      required: true,
    },
    facet: {
      type: String,
      required: false,
      default: () => {
        return 'tags.name'
      },
    },
    customTitle: {
      type: String,
      required: false,
    },
    swiperClass: {
      type: String,
      required: true,
    },
  },
  components: {
    Swiper,
  },
  methods: {
    searchTopic(query) {
      this.$store.dispatch('search/setQuery', query)
      this.$router.push({ name: 'search' })
    },
    handleResults(index, { hits }) {
      this.courses = hits
    },
    createAlgoliaSearchInstance() {
      const {
        algoliaKeys: { appKey, appId, coursesIndex: courses },
      } = this
      const setup = {
        keys: {
          appId,
          appKey,
        },
        contexts: [
          {
            name: 'Courses',
            index: courses,
            facets: [this.facet],
          },
        ],
        resultsHandler: this.handleResults,
      }
      const parentFilter = {
        facet: this.facet,
        value: this.tag,
      }
      createAlgoliaIndices(setup, parentFilter)
    },
    getInstructor(course) {
      return getEntities.getInstructorName(course)
    },
    getVendor(course) {
      return getEntities.getVendorName(course)
    },
    getLearningArea(course) {
      return getEntities.getLearningAreaName(course)
    },
    getLearningAreaColor(course) {
      return `--la: ${getLearningAreaSnipeColor(this.getLearningArea(course))}`
    },
    courseDuration(course) {
      const duration_in_seconds = duration.convertToSeconds(course?.duration)
      return duration.durationFromSecs(duration_in_seconds)
    },
  },
  created() {
    this.createAlgoliaSearchInstance()
  },
  watch: {
    tag() {
      this.createAlgoliaSearchInstance()
    },
    facet() {
      this.createAlgoliaSearchInstance()
    },
  },
}
</script>

<style lang="scss" scoped>
.mobile-course-carousel {
  $this: &;
  padding-right: 0;
  padding-bottom: 50px;
  &__link-wrapper {
    margin: 0 0 20px 20px;
  }
  &__link {
    color: var(--white);
    font-size: rem(20);
    line-height: 26px;
    font-weight: 700;
    transition: all 0.5s ease;
    text-decoration: none;
    cursor: pointer;
    margin: 0;
    &:hover {
      color: var(--orange);
    }
  }
  &__tile {
    text-align: left;
    height: 171px;
    width: 280px;
    color: var(--white);
    position: relative;
    background-color: var(--orange);
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center;
    border-radius: 10px;
    &:hover {
      cursor: pointer;
      #{$this}__overlay {
        opacity: 1;
      }
    }
  }
  &__overlay {
    position: absolute;
    height: 100%;
    width: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    right: 0;
    top: 0;
    z-index: 0;
    opacity: 0;
    transition: 0.5s all ease;
  }
  &__course-link {
    color: var(--white);
    text-decoration: none;
    height: 100%;
    z-index: 1;
    &:hover {
      color: var(--white);
      text-decoration: none;
    }
  }
  &__details {
    display: flex;
    flex-direction: column;
    width: 100%;
    padding: 20px;
    height: 100%;
    box-sizing: border-box;
  }
  &__title {
    font-size: rem(14);
    line-height: 20px;
    font-weight: 400;
    width: 202px;
    z-index: 10;
  }
  &__tile-bottom {
    display: flex;
    flex-direction: column;
    margin-top: auto;
  }
  &__info-wrapper {
    display: flex;
    width: 100%;
    justify-content: space-between;
  }
  &__name,
  &__name--short {
    font-size: rem(14);
    color: var(--white);
    font-family: var(--font-book);
    line-height: 20px;
    max-width: 253px;
    margin: 0;
    @include tablet {
      font-size: rem(18);
      line-height: 24px;
    }
    &--short {
      display: none;
    }
  }
  &__name.course-tile__name--toggle {
    @include mobile {
      display: none;
    }
    @media screen and (min-width: 1390px) {
      display: block;
    }
  }
  &__name--short.course-tile__name--toggle {
    display: none;
    @include mobile {
      display: block;
    }
    @media screen and (min-width: 1390px) {
      display: none;
    }
  }

  &__statistics-wrapper {
    width: 100%;
    margin-top: 10px;
    color: var(--white);
    font-size: rem(10px);
    line-height: 12px;
    display: flex;
    @include tablet {
      font-size: rem(12);
      line-height: 14px;
    }
  }
  &__statistics-separator {
    margin: 0 5px;
  }
  &__course-length {
    text-transform: uppercase;
  }
  &__difficulty {
    margin: 0 0 15px 0;
    z-index: 10;
  }
  &__instructor-wrapper {
    position: relative;
  }
  &__instructor-img {
    position: absolute;
    left: 0;
    border-radius: 100%;
    max-height: 50px;
    top: -48px;
    @include tablet {
      top: -70px;
      max-height: 75px;
    }
  }
  &__instructor-name {
    font-size: rem(10);
    line-height: 12px;
    color: var(--white);
    margin: 15px 0 0 0;
    @include tablet {
      font-size: rem(14);
      line-height: 20px;
    }
  }
  &__vendor-wrapper {
    position: relative;
  }
  &__learning-area {
    text-transform: uppercase;
    color: transparent;
    font-size: rem(10);
    position: relative;
    margin: 0;
    padding: 0;
    transition: 0.2s all ease;
    white-space: nowrap;
    width: 100%;
    &:before {
      content: '';
      position: absolute;
      opacity: 0;
      width: 10px;
      height: 10px;
      border-radius: 5px;
      background: var(--la);
    }
  }
  &__vendor-name {
    text-transform: uppercase;
    color: var(--white);
    font-size: rem(10);
    line-height: 12px;
    @include tablet {
      font-size: rem(14);
      line-height: 20px;
    }
    position: relative;
    margin: 0;
    padding: 0;
    text-align: right;
    width: 100%;
    &:before {
      position: absolute;
      top: -14.5px;
      left: calc(100% - 35px);
      width: 35px;
      height: 8px;
      content: '';
      background: var(--la);
      border-radius: 5px;
      transition: 0.3s all;
    }
  }
  &__snipe {
    position: absolute;
    top: 0;
    right: 5px;
    background: var(--black);
    color: var(--white);
    text-transform: uppercase;
    font-size: 12px;
    font-weight: 700;
    padding: 7px 10px;
  }
  &__view-all {
    font-size: rem(14);
    line-height: 20px;
    margin-top: 20px;
    color: var(--white);
    padding: 8px 16px;
    display: flex;
    white-space: nowrap;
    align-items: center;
    justify-content: flex-end;
    transition: 0.5s all ease;
    text-decoration: none;
    cursor: pointer;
    &:hover {
      cursor: pointer;
      color: var(--orange);
      text-decoration: none;
    }
  }
  &__swiper-container {
    padding-left: 20px;
    mask-image: linear-gradient(
      to right,
      transparent 0%,
      #030303 10%,
      #030303 90%,
      transparent 100%
    );
  }
}
</style>
